/**
 * Importing bank exports and card statements.
 *
 * The flow: hash the file (the identical file is refused), read it into `StatementRow`s, plan
 * the import against what the account already holds (`lib/import/reconcile.ts`), show the plan,
 * and only then write it. Rows are written before the import record, so an import that dies
 * halfway can simply be run again: everything it already wrote is then recognised as known.
 */
import type { StatementImportsResponse } from "@/lib/platform/pb-types";
import { env } from "@/lib/platform/env";
import { pb } from "@/lib/platform/pocketbase";
import {
  combineDuplicate,
  mergedTransaction,
  newTransaction,
  toExistingRow,
  type ExistingRow,
  type ImportPlan,
  type TransactionFields,
} from "@/lib/import/reconcile";
import {
  rowsFromCardStatement,
  rowsFromGeorgeJson,
  type ParsedCardStatement,
  type SourceFamily,
  type StatementFormat,
  type StatementRow,
} from "@/lib/import/statement";
import { readXlsxTable } from "@/lib/import/xlsx";
import { decodeCsv, parseCsv } from "@/lib/utils/csv";
import type { Transaction } from "./banking";

export type StatementImport = StatementImportsResponse;

export { hashFile } from "@/lib/utils/hash";

export async function findImport(
  accountId: string,
  fileHash: string,
): Promise<StatementImport | null> {
  const found = await pb.collection("statement_imports").getList<StatementImport>(1, 1, {
    filter: pb.filter("account = {:accountId} && fileHash = {:fileHash}", { accountId, fileHash }),
  });
  return found.items[0] ?? null;
}

export async function listImports(workspaceId: string): Promise<StatementImport[]> {
  return pb.collection("statement_imports").getFullList<StatementImport>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    sort: "-created",
  });
}

/** Sends a card statement PDF to services/ai, which reads its table. */
export async function parseStatementPdf(file: File): Promise<ParsedCardStatement> {
  const form = new FormData();
  form.set("file", file);
  const res = await fetch(`${env.aiUrl}/statement`, {
    method: "POST",
    headers: { Authorization: `Bearer ${pb.authStore.token}` },
    body: form,
  });
  if (!res.ok) {
    let message = `Statement could not be read (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep the status message
    }
    throw new Error(message);
  }
  return (await res.json()) as ParsedCardStatement;
}

/**
 * A file read but not yet mapped. Tables (CSV, XLSX) still need their columns assigned, which the
 * dialog lets the user correct; JSON and PDF come out as rows directly.
 */
export type ReadStatement =
  | { kind: "table"; format: "csv" | "xlsx"; family: "export"; table: string[][] }
  | {
      kind: "rows";
      format: "json" | "pdf";
      family: SourceFamily;
      rows: StatementRow[];
      cardNumber?: string;
      statementNumber?: string;
    };

export async function readStatementFile(
  file: File,
  format: StatementFormat,
): Promise<ReadStatement> {
  if (format === "pdf") {
    const statement = await parseStatementPdf(file);
    return {
      kind: "rows",
      format,
      family: "statement",
      rows: rowsFromCardStatement(statement),
      cardNumber: statement.cardNumber,
      statementNumber: statement.statementNumber,
    };
  }
  if (format === "json") {
    const text = decodeCsv(await file.arrayBuffer()).replace(/^\uFEFF/, "");
    return { kind: "rows", format, family: "export", rows: rowsFromGeorgeJson(JSON.parse(text)) };
  }
  if (format === "xlsx") {
    return { kind: "table", format, family: "export", table: await readXlsxTable(file) };
  }
  return {
    kind: "table",
    format,
    family: "export",
    table: parseCsv(decodeCsv(await file.arrayBuffer())),
  };
}

/** Every transaction of the account, by id, and reduced to what matching needs. */
export async function loadAccountRows(
  accountId: string,
): Promise<{ records: Map<string, Transaction>; existing: ExistingRow[] }> {
  const list = await pb.collection("transactions").getFullList<Transaction>({
    filter: pb.filter("account = {:accountId}", { accountId }),
  });
  return {
    records: new Map(list.map((record) => [record.id, record])),
    existing: list.map((record) => toExistingRow(record)),
  };
}

/** PocketBase ids are 15 lower-case alphanumerics; pre-generating one lets rows point at the import. */
export function newRecordId(): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = crypto.getRandomValues(new Uint8Array(15));
  return [...bytes].map((byte) => alphabet[byte % alphabet.length]).join("");
}

export type ImportFile = {
  name: string;
  hash: string;
  format: StatementFormat;
  family: SourceFamily;
  statementNumber?: string;
};

const stored = (record: Transaction): Partial<TransactionFields> => ({
  ...record,
  sourceKeys: Array.isArray(record.sourceKeys) ? (record.sourceKeys as string[]) : [],
  confirmedBy: Array.isArray(record.confirmedBy) ? (record.confirmedBy as string[]) : [],
});

export async function commitImport(args: {
  workspaceId: string;
  accountId: string;
  userId: string;
  file: ImportFile;
  plan: ImportPlan;
  records: Map<string, Transaction>;
}): Promise<StatementImport> {
  const { workspaceId, accountId, userId, file, plan, records } = args;
  const importId = newRecordId();

  for (const item of plan.items) {
    if (item.kind === "create" || item.kind === "review") {
      await pb.collection("transactions").create({
        ...newTransaction(item.row, item.key, importId),
        workspace: workspaceId,
        account: accountId,
        status: "unmatched",
        importBatch: importId,
        possibleDuplicateOf: item.kind === "review" ? item.duplicateOf : "",
      });
    } else if (item.kind === "merge") {
      const record = records.get(item.existingId);
      if (!record) continue;
      await pb
        .collection("transactions")
        .update(item.existingId, mergedTransaction(stored(record), item.row, item.key, importId));
    }
  }

  const dates = plan.items.map((item) => item.row.date).sort();
  return pb.collection("statement_imports").create<StatementImport>({
    id: importId,
    workspace: workspaceId,
    account: accountId,
    fileName: file.name,
    fileHash: file.hash,
    format: file.format,
    family: file.family,
    statementNumber: file.statementNumber ?? "",
    periodFrom: dates[0] ?? "",
    periodTo: dates[dates.length - 1] ?? "",
    rowCount: plan.items.length,
    createdCount: plan.counts.create,
    knownCount: plan.counts.known,
    mergedCount: plan.counts.merge,
    reviewCount: plan.counts.review,
    excludedCount: plan.counts.excluded,
    importedBy: userId,
  });
}

/**
 * Takes an import back: deletes the rows it created that nobody has worked on since, and the
 * import record. Rows that got a receipt or were confirmed by another import stay - deleting
 * them would lose work. Details an import added to rows that already existed stay as well.
 */
export async function undoImport(
  record: StatementImport,
): Promise<{ deleted: number; kept: number }> {
  const rows = await pb.collection("transactions").getFullList<Transaction>({
    filter: pb.filter("importBatch = {:id}", { id: record.id }),
  });
  let deleted = 0;
  let kept = 0;
  for (const row of rows) {
    const confirmations = Array.isArray(row.confirmedBy) ? (row.confirmedBy as string[]) : [];
    // A receipt the matcher linked on its own is not work anyone did; the document stays.
    const untouched = (row.status === "unmatched" && !row.document) || row.matchedBy === "auto";
    const onlyThisImport = confirmations.every((id) => id === record.id);
    if (untouched && onlyThisImport) {
      await pb.collection("transactions").delete(row.id);
      deleted += 1;
    } else {
      kept += 1;
    }
  }
  await pb.collection("statement_imports").delete(record.id);
  return { deleted, kept };
}

/** "Same charge": fold the flagged row into its twin and delete it. "Different": clear the flag. */
export async function resolveDuplicate(rowId: string, decision: "merge" | "keep"): Promise<void> {
  if (decision === "keep") {
    await pb.collection("transactions").update(rowId, { possibleDuplicateOf: "" });
    return;
  }
  const duplicate = await pb.collection("transactions").getOne<Transaction>(rowId);
  const target = await pb
    .collection("transactions")
    .getOne<Transaction>(duplicate.possibleDuplicateOf);
  await pb
    .collection("transactions")
    .update(
      target.id,
      combineDuplicate(
        { ...stored(target), status: target.status, document: target.document },
        { ...stored(duplicate), status: duplicate.status, document: duplicate.document },
      ),
    );
  await pb.collection("transactions").delete(duplicate.id);
}
